import { jwtDecode } from "jwt-decode";
import {
  PropsWithChildren,
  createContext,
  useEffect,
  useMemo,
  useState,
} from "react";
import { useNavigate } from "react-router-dom";
import { Jwt } from "../models/Auth";
import { JwtService } from "../services/api/JwtService";

type AuthProviderContextType = {
  isLoggedIn: boolean;
  token: string | null;
  login: (token: string) => void;
  tokenExpiry: number | undefined;
  userName: string;
  claims: string[];
  logout: () => void;
  setUserName: React.Dispatch<React.SetStateAction<string>>;
  contactId: number | undefined;
  setContactId: React.Dispatch<React.SetStateAction<number | undefined>>;
  hasClaim: (claim: string) => boolean;  
};

const AuthContext = createContext({} as AuthProviderContextType);

export const AuthProvider = ({ children }: PropsWithChildren) => {
  const navigate = useNavigate();
  const [token, setToken] = useState<string | null>(JwtService.get());
  const [tokenExpiry, setTokenExpiry] = useState<number | undefined>(() => {
    const accessToken = JwtService.get();
    if (accessToken) {
      const decoded = jwtDecode<Jwt>(accessToken);      
      return decoded?.exp
    }
    return;
  });
  const [userName, setUserName] = useState(() => {
    const accessToken = JwtService.get();
    if (accessToken) {
      const decoded = jwtDecode<Jwt>(accessToken);
      return decoded?.nameid;
    }
    return "";
  });
  const [claims, setClaims] = useState<string[]>(() => {
    const accessToken = JwtService.get();
    if (accessToken) {
      const decoded = jwtDecode<Jwt>(accessToken);
      return decoded["https://storefinancial.net/permission"];
    }
    return [];
  });
  const [contactId, setContactId] = useState<number | undefined>(() => {
    const accessToken = JwtService.get();
    if (accessToken) {
      const decoded = jwtDecode<Jwt>(accessToken);
      const cid = parseInt(decoded["https://storefinancial.net/contactId"]);
      if (!isNaN(cid)) return cid;
    }
    return;
  });
  const [isLoggedIn, setIsLoggedIn] = useState(() => {
    const accessToken = JwtService.get();
    if (accessToken) {
      const decoded = jwtDecode<Jwt>(accessToken);
      const isExpired = (decoded?.exp ?? 0) * 1000 < Date.now();
      return !isExpired;
    }
    return false;
  });

  useEffect(() => {
    const accessToken = JwtService.get();
    setToken(accessToken);
    if (accessToken) {
      login(accessToken);
    } else {
      navigate("/login", { replace: true });
    }
  }, []);

  const login = (accessToken: string) => {
    setToken(accessToken);
    JwtService.set(accessToken);
    const decoded = jwtDecode<Jwt>(accessToken);
    setTokenExpiry(decoded?.exp);
    setUserName(decoded?.nameid);
    const cid = parseInt(decoded["https://storefinancial.net/contactId"]);
    if (!isNaN(cid)) setContactId(cid);
    setClaims(decoded["https://storefinancial.net/permission"]);
    const isExpired = (decoded?.exp ?? 0) * 1000 < Date.now();
    setIsLoggedIn(token !== null && !isExpired);
  };

  const logout = () => {
    JwtService.remove();
    setUserName("");
    setIsLoggedIn(false);
    setToken(null);
    // this will reload the page and reset the state of the application
    navigate("/login");
  };  

  const hasClaim = (claim: string) => {
    return claims?.includes(claim) ?? false;
  };

  const contextValue = useMemo(
    () => ({
      isLoggedIn,
      token,
      login,
      tokenExpiry,
      userName,
      claims,
      logout,
      setUserName,
      contactId,
      setContactId,
      hasClaim,      
    }),
    [token]
  );

  return (
    <AuthContext.Provider value={contextValue}>{children}</AuthContext.Provider>
  );
};

export default AuthContext;
